import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Checkbox,
} from '@mui/material';
import { HiX } from 'react-icons/hi';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useActivityQuery, useChildOrgsOrdered } from '@/api/hooks/useActivityLog';
import { activityLogService } from '@/api/services/activity-log.service';
import { PRIVILEGES } from '@/api/types/dtos';
import { useHasPrivilege } from '@/components/authorization';
import { useReportOrgSelection } from '@/hooks/useReportOrgSelection';
import { useFetchAllOrgIds } from '@/hooks/useFetchAllOrgIds';
import ReportRequestDialog from '@/components/reports/ReportRequestDialog';
import type { ActivityQueryType } from '@/api/types/dtos/activity-log/activity-log.dto';
import FarmDetailsModal from '@/components/modal/FarmDetailsModal';
import { getActivityTypeLabel } from '../activityLabels';

const QUERY_OPTIONS: { value: ActivityQueryType; label: string }[] = [
  { value: 'ORGS_NOT_TRAINED_IN_30D', label: 'Orgs sin capacitación en los últimos 30 días' },
  { value: 'ORGS_NOT_INSPECTED_IN_90D', label: 'Orgs sin retos en los últimos 90 días' },
  { value: 'ORGS_WITH_NO_INFO_UPDATE_30D', label: 'Orgs sin actualización de info en 30 días' },
  { value: 'ORGS_NEVER_INSPECTED', label: 'Orgs que nunca han tenido un reto' },
  { value: 'ORGS_INACTIVE', label: 'Organizaciones inactivas' },
];

const ACTIVITY_TYPE_OPTIONS = [
  { value: '', label: 'Todos los Tipos' },
  { value: 'TRAINING', label: 'Capacitación' },
  { value: 'INSPECTION', label: 'Retos' },
  { value: 'INSPECTION_ITEM', label: 'Ítem de Reto' },
  { value: 'ORG_INFO', label: 'Información' },
  { value: 'ORG_CREATED', label: 'Creado' },
];

const QuickQueryBar: React.FC = () => {
  const [selectedQuery, setSelectedQuery] = useState<ActivityQueryType | ''>('');
  const [activeQuery, setActiveQuery] = useState<ActivityQueryType | null>(null);
  const [showAllOrgs, setShowAllOrgs] = useState(true);
  const [activityType, setActivityType] = useState('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [farmDetailsOpen, setFarmDetailsOpen] = useState(false);
  const [queryPage, setQueryPage] = useState(0);
  const [queryRowsPerPage, setQueryRowsPerPage] = useState(20);
  const [orgsPage, setOrgsPage] = useState(0);
  const [orgsRowsPerPage, setOrgsRowsPerPage] = useState(20);

  const { data: queryData } = useActivityQuery(activeQuery ? { query: activeQuery, size: queryRowsPerPage, page: queryPage } : null);
  const { data: orgsData, isLoading: orgsLoading } = useChildOrgsOrdered(
    showAllOrgs
      ? {
          sortBy: activityType ? 'entityType' : 'updatedAt',
          entityType: activityType || undefined,
          fromDate: fromDate ? fromDate.toISOString() : undefined,
          toDate: toDate ? toDate.toISOString() : undefined,
          size: orgsRowsPerPage,
          page: orgsPage,
        }
      : undefined
  );

  // Report-from-anywhere state (only for users with CREATE_REPORT)
  const { hasPrivilege: canCreateReport } = useHasPrivilege(PRIVILEGES.CREATE_REPORT);
  const selection = useReportOrgSelection();
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  // Fetch all orgs matching the currently active list's filters
  const { fetchAll: fetchAllOrgIds, isLoading: selectingAllForReport, progress: selectAllProgress } =
    useFetchAllOrgIds(
      React.useCallback(
        async (page: number, size: number) => {
          if (activeQuery) {
            const res = await activityLogService.queryOrgs({ query: activeQuery, page, size });
            const content = res.data?.content ?? [];
            return {
              items: content.map((org) => ({
                id: String(org.id ?? ''),
                name: String(org.name ?? ''),
              })),
              totalElements: res.data?.totalElements ?? 0,
              totalPages: res.data?.totalPages ?? 0,
            };
          }
          const res = await activityLogService.getChildOrgsOrdered({
            sortBy: activityType ? 'entityType' : 'updatedAt',
            entityType: activityType || undefined,
            fromDate: fromDate ? fromDate.toISOString() : undefined,
            toDate: toDate ? toDate.toISOString() : undefined,
            page,
            size,
          });
          const content = res.data?.content ?? [];
          return {
            items: content.map((org) => ({ id: org.id, name: org.name })),
            totalElements: res.data?.totalElements ?? 0,
            totalPages: res.data?.totalPages ?? 0,
          };
        },
        [activeQuery, activityType, fromDate, toDate]
      )
    );

  const handleSelectAllForReport = async () => {
    const orgs = await fetchAllOrgIds();
    if (orgs.length > 0) {
      selection.selectMultiple(orgs);
    }
  };

  const handleQuery = () => {
    if (selectedQuery) {
      setActiveQuery(selectedQuery as ActivityQueryType);
      setShowAllOrgs(false);
      setQueryPage(0);
    }
  };

  const handleShowAllOrgs = () => {
    setShowAllOrgs(true);
    setActiveQuery(null);
    setOrgsPage(0);
  };

  const handleClearDates = () => {
    setFromDate(null);
    setToDate(null);
    setOrgsPage(0);
  };

  const handleOrgRowClick = (orgId: string) => {
    setSelectedOrgId(orgId);
    setFarmDetailsOpen(true);
  };

  const handleFarmDetailsClose = () => {
    setFarmDetailsOpen(false);
    setSelectedOrgId(null);
  };

  const queryOrgs = queryData?.content ?? [];
  const allOrgs = orgsData?.content ?? [];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Consultar Organizaciones
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Consulta Predefinida</InputLabel>
          <Select
            value={selectedQuery}
            label="Consulta Predefinida"
            onChange={(e) => setSelectedQuery(e.target.value as ActivityQueryType)}
          >
            <MenuItem value="">
              <em>Elige una consulta...</em>
            </MenuItem>
            {QUERY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleQuery}
          disabled={!selectedQuery}
        >
          Ejecutar Consulta
        </Button>
        <Button
          variant={showAllOrgs ? 'contained' : 'outlined'}
          onClick={handleShowAllOrgs}
        >
          Todas las Orgs por Actividad
        </Button>
        {showAllOrgs && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Tipo de Actividad</InputLabel>
            <Select
              value={activityType}
              label="Tipo de Actividad"
              onChange={(e) => {
                setActivityType(e.target.value);
                setOrgsPage(0);
              }}
            >
              {ACTIVITY_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {showAllOrgs && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <DatePicker
              label="Desde"
              value={fromDate}
              onChange={(v) => { setFromDate(v); setOrgsPage(0); }}
              slotProps={{
                textField: { size: 'small', sx: { minWidth: 160 } },
              }}
            />
            <DatePicker
              label="Hasta"
              value={toDate}
              onChange={(v) => { setToDate(v); setOrgsPage(0); }}
              minDate={fromDate ?? undefined}
              slotProps={{
                textField: { size: 'small', sx: { minWidth: 160 } },
              }}
            />
            {(fromDate || toDate) && (
              <Tooltip title="Limpiar fechas">
                <IconButton size="small" onClick={handleClearDates}>
                  <HiX size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </LocalizationProvider>
      )}

      {activeQuery && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2">
              {QUERY_OPTIONS.find((o) => o.value === activeQuery)?.label} &mdash; {queryData?.totalElements ?? 0} resultados
            </Typography>
            {canCreateReport && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleSelectAllForReport}
                  disabled={selectingAllForReport}
                >
                  {selectingAllForReport
                    ? `${selectAllProgress?.loaded ?? 0}/${selectAllProgress?.total || '?'}`
                    : 'Seleccionar todo el filtro'}
                </Button>
                <Typography variant="body2" color="textSecondary">
                  {selection.selectedCount} seleccionada{selection.selectedCount === 1 ? '' : 's'}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setReportDialogOpen(true)}
                  disabled={selection.selectedCount === 0}
                >
                  Descargar Reporte
                </Button>
              </Box>
            )}
          </Box>

          {queryOrgs.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No se encontraron organizaciones con esta consulta.
            </Typography>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {canCreateReport && <TableCell padding="checkbox" />}
                  <TableCell>Nombre</TableCell>
                  <TableCell>Número de ID</TableCell>
                  <TableCell>Activa</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queryOrgs.map((org: Record<string, unknown>) => (
                  <TableRow
                    key={org.id as string}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOrgRowClick(org.id as string)}
                  >
                    {canCreateReport && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selection.isSelected(String(org.id ?? ''))}
                          onChange={() =>
                            selection.toggleSelection({
                              id: String(org.id ?? ''),
                              name: String(org.name ?? ''),
                            })
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell>{org.name as string}</TableCell>
                    <TableCell>{(org.idNumber as string) || '\u2014'}</TableCell>
                    <TableCell>{org.isActive ? 'Sí' : 'No'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {queryData && (
            <TablePagination
              component="div"
              count={queryData.totalElements}
              page={queryPage}
              onPageChange={(_, newPage) => setQueryPage(newPage)}
              rowsPerPage={queryRowsPerPage}
              onRowsPerPageChange={(e) => {
                setQueryRowsPerPage(parseInt(e.target.value, 10));
                setQueryPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          )}
        </Box>
      )}

      {showAllOrgs && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2">
              {activityType ? `Orgs por Actividad de ${getActivityTypeLabel(activityType)}` : 'Todas las Orgs por Última Actividad'} &mdash; {orgsData?.totalElements ?? 0} orgs
            </Typography>
            {canCreateReport && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleSelectAllForReport}
                  disabled={selectingAllForReport}
                >
                  {selectingAllForReport
                    ? `${selectAllProgress?.loaded ?? 0}/${selectAllProgress?.total || '?'}`
                    : 'Seleccionar todo el filtro'}
                </Button>
                <Typography variant="body2" color="textSecondary">
                  {selection.selectedCount} seleccionada{selection.selectedCount === 1 ? '' : 's'}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setReportDialogOpen(true)}
                  disabled={selection.selectedCount === 0}
                >
                  Descargar Reporte
                </Button>
              </Box>
            )}
          </Box>

          {allOrgs.length === 0 && !orgsLoading && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No se encontraron organizaciones.
            </Typography>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {canCreateReport && <TableCell padding="checkbox" />}
                  <TableCell>Nombre</TableCell>
                  <TableCell>Número de ID</TableCell>
                  <TableCell>Activa</TableCell>
                  <TableCell>Última Actividad</TableCell>
                  <TableCell>Tipos de Actividad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allOrgs.map((org) => (
                  <TableRow
                    key={org.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOrgRowClick(org.id)}
                  >
                    {canCreateReport && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selection.isSelected(org.id)}
                          onChange={() => selection.toggleSelection({ id: org.id, name: org.name })}
                        />
                      </TableCell>
                    )}
                    <TableCell>{org.name}</TableCell>
                    <TableCell>{org.idNumber || '\u2014'}</TableCell>
                    <TableCell>{org.isActive ? 'Sí' : 'No'}</TableCell>
                    <TableCell>
                      {org.lastActivityOverall
                        ? new Date(org.lastActivityOverall).toLocaleDateString()
                        : 'Nunca'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {Object.entries(org.lastActivityByType).map(([type, date]) => (
                          <Chip
                            key={type}
                            label={`${getActivityTypeLabel(type)}: ${new Date(date).toLocaleDateString()}`}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {orgsData && (
            <TablePagination
              component="div"
              count={orgsData.totalElements}
              page={orgsPage}
              onPageChange={(_, newPage) => setOrgsPage(newPage)}
              rowsPerPage={orgsRowsPerPage}
              onRowsPerPageChange={(e) => {
                setOrgsRowsPerPage(parseInt(e.target.value, 10));
                setOrgsPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          )}
        </Box>
      )}

      {/* Farm Details Modal */}
      {selectedOrgId && (
        <FarmDetailsModal
          open={farmDetailsOpen}
          onClose={handleFarmDetailsClose}
          organizationId={selectedOrgId}
        />
      )}

      {canCreateReport && (
        <ReportRequestDialog
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          orgs={selection.selectedOrgs}
          sourceLabel={activeQuery ? 'Consulta de actividad' : 'Orgs por actividad'}
        />
      )}
    </Box>
  );
};

export default QuickQueryBar;
